import styled from "styled-components";
import { Attribute, AttributesView, Card } from "@bagel-web/components";
import {
  AgentConfig,
  CommonComponentPresentation,
  CommonComponentState,
  CommonTypedComponentState,
  PlaygroundSeedMessage,
} from "../../types";
import SimpleComponentVisualization from "./SimpleComponentVisualization";
import LimitedListVisualization from "./limitedList/LimitedListVisualization";
import KVStoreVisualization from "./kvStore/KVStoreVisualization";
import { AgencyTagFilter, getAgencyTagFilter } from "../../tag/AgencyTagFilter";
import getLimitedListHeaderFields from "./limitedList/LimitedListHeaderFields";
import { ComponentTypeIcon } from "./ComponentTypeIcon";

const VisualizationContainer = styled.div`
  font-size: 0.875rem;
`;

// The filter a caller without a tag filter control gets: every item shows.
const MATCH_ALL_TAGS = getAgencyTagFilter([]);

export interface ASHComponentVisualizationProps<
  T extends CommonComponentState,
> {
  componentState?: T;
  componentPresentation?: CommonComponentPresentation;
  sessionId: string;
  agentId: string;
  agencyTagFilter: AgencyTagFilter;
  isEditMode?: boolean;

  // Optionally provide the seed messages from which component state was derived
  messages?: Array<PlaygroundSeedMessage>;

  // Callback to update the seed messages when in edit mode
  onMessagesChange?: (messages: Array<PlaygroundSeedMessage>) => void;
}

export type ASHComponentVisualization<T extends CommonComponentState> =
  React.FC<ASHComponentVisualizationProps<T>>;

const getVisualizationComponent = (
  componentType?: string
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
ASHComponentVisualization<any> => {
  switch (componentType) {
    case "puppeteer_limited_list":
    case "limited_list":
      return LimitedListVisualization;
    case "kv_store":
    case "puppeteer_kv_store":
      return KVStoreVisualization;
    default:
      return SimpleComponentVisualization;
  }
};

export interface ASHComponentHeaderAttributesProps<
  T extends CommonComponentState,
> {
  componentState?: T;
  componentPresentation?: CommonComponentPresentation;
  agentConfig?: AgentConfig;
  sessionId: string;
  agentId: string;
}

export type ASHComponentCardAttributes<T extends CommonComponentState> = (
  props: ASHComponentHeaderAttributesProps<T>
) => Array<Attribute>;

const curriedGetHeaderAttributes = (
  componentType?: string
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
ASHComponentCardAttributes<any> => {
  if (componentType === "limited_list") {
    return getLimitedListHeaderFields;
  }
  return () => [];
};

const ASHComponentCard = ({
  componentState,
  componentPresentation,
  sessionId,
  agentId,
  agentConfig,
  agencyTagFilter = MATCH_ALL_TAGS,
  isEditMode,
  messages,
  onMessagesChange,
}: {
  componentState?: CommonTypedComponentState;
  componentPresentation?: CommonComponentPresentation;
  sessionId: string;
  agentId: string;
  agentConfig?: AgentConfig;
  agencyTagFilter?: AgencyTagFilter;
  isEditMode?: boolean;
  messages?: Array<PlaygroundSeedMessage>;
  onMessagesChange?: (messages: Array<PlaygroundSeedMessage>) => void;
}) => {
  const Visualization = getVisualizationComponent(componentState?.type);
  const getHeaderAttributes = curriedGetHeaderAttributes(componentState?.type);

  return (
    <Card
      collapsible={true}
      Header={() => {
        return (
          <div className="d-flex flex-row gap-5 align-items-center mb-3">
            <div className="d-flex flex-row align-items-center gap-3 fs-5">
              <ComponentTypeIcon type={componentState?.type} />
              <code>
                <b>{componentState?.id}</b>
              </code>
            </div>
            <AttributesView
              className="flex-grow-1 flex-shrink-1"
              columns={true}
              attributes={[
                {
                  name: "type",
                  label: "Component Type",
                  value: <code>{componentState?.type}</code>,
                },
                ...getHeaderAttributes({
                  componentState,
                  componentPresentation,
                  sessionId,
                  agentId,
                  agentConfig,
                }),
              ]}
            />
          </div>
        );
      }}
    >
      <VisualizationContainer>
        <Visualization
          componentState={componentState}
          componentPresentation={componentPresentation}
          sessionId={sessionId}
          agentId={agentId}
          agencyTagFilter={agencyTagFilter}
          isEditMode={isEditMode}
          messages={messages}
          onMessagesChange={onMessagesChange}
        />
      </VisualizationContainer>
    </Card>
  );
};

export default ASHComponentCard;
